/**
 * API Client — fetch wrapper pour communiquer avec l'API Synapse
 * Types partagés depuis @synapse/shared
 *
 * SH-01: ce module est le POINT D'ENTRÉE UNIQUE pour tous les appels réseau.
 * Les composants/pages ne doivent PAS faire de `fetch` brut — ils importent
 * `api` et utilisent les méthodes typées ci-dessous.
 */

import type {
  Project,
  Mission,
  CrewMember,
  Notification,
  AuditLog,
  CicdEvent,
  ScheduledTask,
  ScheduledRun,
  Setting,
  Commander,
  CreateCommanderInput,
  UpdateCommanderInput,
  ClusterHealth,
  ResourcesUsage,
} from "@synapse/shared";

const BASE = import.meta.env.VITE_API_URL || "/api";

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || `API Error ${res.status}`);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── Types de réponse non couverts par shared ────────────────────

export interface ChannelsHealth {
  discord: string;
  obsidian: string;
  github: string;
}

export interface IntegrationHealth {
  key: string;
  label: string;
  status: "ok" | "degraded" | "down" | "unconfigured";
  detail?: string;
  latencyMs?: number;
}

export interface SettingTestResult {
  ok: boolean;
  latencyMs?: number;
  message: string;
}

export interface DrpStep {
  id: number;
  icon: string;
  title: string;
  detail: string;
  cmds: string;
  eta: string;
  deps: string;
  enabled: boolean;
  state: "pending" | "active" | "done" | "error";
}

export interface SearchHit {
  id: string;
  type: "mission" | "project" | "crew" | "log" | "setting";
  title: string;
  subtitle: string;
}

export interface GitHubProjectsSyncResult {
  ok: boolean;
  organization: string;
  fetchedRepos: number;
  linked: number;
  created: number;
  ignored: number;
}

/** Aperçu retourné par la sync en mode dry-run (aucune écriture en base). */
export interface GitHubProjectsSyncPreview {
  dryRun: true;
  organization: string;
  fetchedRepos: number;
  ignored: number;
  linked: number;
  created: number;
  willLink: Array<{ projectId: string; repo: string }>;
  willCreate: Array<{ name: string; repo: string }>;
}

// ────────────────────────────────────────────────────────────────

export const api = {
  // ── Projects ──────────────────────────────────────────────────
  projects: {
    list: () => apiFetch<Project[]>("/projects"),
    get: (id: string) => apiFetch<Project>(`/projects/${id}`),
    create: (data: Partial<Project>) =>
      apiFetch<Project>("/projects", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<Project>) =>
      apiFetch<Project>(`/projects/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) =>
      apiFetch<void>(`/projects/${id}`, { method: "DELETE" }),
  },

  github: {
    syncProjects: () =>
      apiFetch<GitHubProjectsSyncResult>("/projects/sync/github", { method: "POST", body: "{}" }),
    previewProjectsSync: () =>
      apiFetch<GitHubProjectsSyncPreview>("/projects/sync/github", {
        method: "POST",
        body: JSON.stringify({ dryRun: true }),
      }),
  },

  // ── Missions ──────────────────────────────────────────────────
  missions: {
    list: (params?: { status?: string; projectId?: string }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set("status", params.status);
      if (params?.projectId) q.set("projectId", params.projectId);
      const qs = q.toString();
      return apiFetch<Mission[]>(`/missions${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => apiFetch<Mission>(`/missions/${id}`),
    create: (data: Partial<Mission> & { title: string; projectId: string }) =>
      apiFetch<Mission>("/missions", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<Mission>) =>
      apiFetch<Mission>(`/missions/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) =>
      apiFetch<void>(`/missions/${id}`, { method: "DELETE" }),
    start: (id: string) =>
      apiFetch<{ missionId: string; status: string }>(`/missions/${id}/start`, { method: "POST" }),
  },

  // ── Crew ──────────────────────────────────────────────────────
  crew: {
    list: () => apiFetch<CrewMember[]>("/crew"),
    get: (id: string) => apiFetch<CrewMember>(`/crew/${id}`),
    create: (data: Partial<CrewMember> & { name: string; role: string }) =>
      apiFetch<CrewMember>("/crew", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<CrewMember>) =>
      apiFetch<CrewMember>(`/crew/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) =>
      apiFetch<void>(`/crew/${id}`, { method: "DELETE" }),
  },

  // ── Notifications ─────────────────────────────────────────────
  notifications: {
    list: (params?: { unreadOnly?: boolean; limit?: number }) => {
      const q = new URLSearchParams();
      if (params?.unreadOnly) q.set("unreadOnly", "true");
      if (params?.limit) q.set("limit", String(params.limit));
      const qs = q.toString();
      return apiFetch<Notification[]>(`/notifications${qs ? `?${qs}` : ""}`);
    },
    markRead: (id: string) =>
      apiFetch<void>(`/notifications/${id}/read`, { method: "PATCH" }),
    markAllRead: () =>
      apiFetch<void>(`/notifications/read-all`, { method: "POST" }),
  },

  // ── Audit log ─────────────────────────────────────────────────
  audit: {
    list: (params?: { limit?: number; severity?: string; category?: string }) => {
      const q = new URLSearchParams();
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.severity) q.set("severity", params.severity);
      if (params?.category) q.set("category", params.category);
      const qs = q.toString();
      return apiFetch<AuditLog[]>(`/audit${qs ? `?${qs}` : ""}`);
    },
  },

  // ── CI/CD events ──────────────────────────────────────────────
  cicd: {
    list: (params?: { limit?: number; project?: string; status?: string }) => {
      const q = new URLSearchParams();
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.project) q.set("project", params.project);
      if (params?.status) q.set("status", params.status);
      const qs = q.toString();
      return apiFetch<CicdEvent[]>(`/cicd/events${qs ? `?${qs}` : ""}`);
    },
  },

  // ── Scheduled tasks ───────────────────────────────────────────
  scheduledTasks: {
    list: () => apiFetch<ScheduledTask[]>("/scheduled-tasks"),
    create: (data: Partial<ScheduledTask> & { name: string; cron: string }) =>
      apiFetch<ScheduledTask>("/scheduled-tasks", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<ScheduledTask>) =>
      apiFetch<ScheduledTask>(`/scheduled-tasks/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) =>
      apiFetch<void>(`/scheduled-tasks/${id}`, { method: "DELETE" }),
    runs: (id: string) =>
      apiFetch<ScheduledRun[]>(`/scheduled-tasks/${id}/runs`),
  },

  // ── Settings ──────────────────────────────────────────────────
  settings: {
    list: () => apiFetch<Setting[]>("/settings"),
    put: (key: string, value: string, isSecret = false) =>
      apiFetch<{ key: string; isSecret: boolean; updatedAt?: string; noop?: boolean }>(
        `/settings/${encodeURIComponent(key)}`,
        { method: "PUT", body: JSON.stringify({ value, isSecret }) },
      ),
    test: (key: string, value?: string) =>
      apiFetch<SettingTestResult>(`/settings/${encodeURIComponent(key)}/test`, {
        method: "POST",
        body: JSON.stringify(value ? { value } : {}),
      }),
  },

  // ── Commanders ────────────────────────────────────────────────
  commanders: {
    list: () => apiFetch<Commander[]>("/commanders"),
    create: (data: CreateCommanderInput) =>
      apiFetch<Commander>("/commanders", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: UpdateCommanderInput) =>
      apiFetch<Commander>(`/commanders/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    delete: (id: string) =>
      apiFetch<void>(`/commanders/${id}`, { method: "DELETE" }),
  },

  // ── Obsidian (plugin Local REST API) ──────────────────────────
  obsidian: {
    test: (apiUrl?: string, apiKey?: string) =>
      apiFetch<{ ok: boolean; latencyMs?: number; message: string; vault?: string; authenticated?: boolean }>(
        "/obsidian/test",
        { method: "POST", body: JSON.stringify({ apiUrl, apiKey }) },
      ),
    provisionLocal: () =>
      apiFetch<{
        ok: boolean; mode: string; apiUrl: string; keyGenerated: boolean;
        keyPreview: string; message: string; sidecarManifest: string; nextSteps: string[];
      }>("/obsidian/provision-local", { method: "POST", body: "{}" }),
  },

  // ── Health ────────────────────────────────────────────────────
  health: {
    channels: () => apiFetch<ChannelsHealth>("/health/channels"),
    integrations: () => apiFetch<IntegrationHealth[]>("/health/integrations"),
  },

  // ── DRP ───────────────────────────────────────────────────────
  drp: {
    steps: () => apiFetch<DrpStep[]>("/drp/steps"),
    execute: (id: number) => apiFetch<unknown>(`/drp/steps/${id}/execute`, { method: "POST" }),
    executeAll: () => apiFetch<unknown>("/drp/execute-all", { method: "POST" }),
    simulate: () => apiFetch<unknown>("/drp/simulate", { method: "POST" }),
    reset: () => apiFetch<unknown>("/drp/reset", { method: "POST" }),
    toggle: (id: number, enabled: boolean) =>
      apiFetch<DrpStep>(`/drp/steps/${id}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),
  },

  // ── Observabilité & Ressources ────────────────────────────────
  cluster: {
    health: () => apiFetch<ClusterHealth>("/cluster/health"),
  },
  resources: {
    get: () => apiFetch<ResourcesUsage>("/resources"),
  },

  // ── Recherche globale ─────────────────────────────────────────
  search: (q: string, scope = "all", limit = 20) => {
    const params = new URLSearchParams({ q, scope, limit: String(limit) });
    return apiFetch<SearchHit[]>(`/search?${params.toString()}`);
  },

  // ── OAuth OpenAI (Device Code via Codex CLI) ──────────────────
  oauthOpenai: {
    status: () => apiFetch<{
      mode: string; configured: boolean; connected: boolean;
      account: string | null; expiresAt: number | null; expired: boolean | null;
    }>("/oauth/openai/status"),
    logout: () => apiFetch<void>("/oauth/openai/logout", { method: "POST", body: "{}" }),
    deviceCode: {
      // Lance le process codex login — retourne immédiatement
      start: () => apiFetch<{ status: string; message?: string }>("/oauth/openai/device-code"),
      // Poll le statut du device code
      poll: () => apiFetch<{
        status: string; verificationUrl?: string; userCode?: string; error?: string;
      }>("/oauth/openai/device-code/poll"),
      // Importe les tokens dans la DB
      complete: () => apiFetch<{ imported: boolean; account: string; message: string }>(
        "/oauth/openai/device-code/complete", { method: "POST", body: "{}" },
      ),
    },
  },
};
